import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  MapContainer, TileLayer, CircleMarker, Popup, Tooltip, useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import {
  Store, Package, AlertTriangle, CheckCircle2, TrendingDown,
  Filter, ArrowRight, ShieldCheck, Layers, Eye, EyeOff,
  Maximize, Minimize,
} from "lucide-react";
import api from "../lib/api";
import { formatRupiah, formatNumber } from "../lib/format";
import { StatCard, Section, Skeleton } from "../components/UI";
import BIPricePanel from "../components/BIPricePanel";
import EwsTable from "../components/EwsTable";

/* ── Constants ─────────────────────────────────── */
const SUMUT_CENTER = [2.3, 99.0];
const SUMUT_BOUNDS = [[-0.5, 96.0], [5.0, 101.5]];

const LEVEL_COLOR = {
  normal: "#10B981",       // Emerald Green
  waspada: "#F59E0B",      // Amber Yellow
  tinggi: "#F97316",       // Orange
  "sangat tinggi": "#EF4444" // Red
};
const LEVEL_LABEL = {
  normal: "Normal",
  waspada: "Waspada",
  tinggi: "Tinggi",
  "sangat tinggi": "Sangat Tinggi"
};
const LEVEL_BG = {
  normal: "bg-emerald-50 text-emerald-700 border-emerald-200",
  waspada: "bg-amber-50 text-amber-700 border-amber-200",
  tinggi: "bg-orange-50 text-orange-700 border-orange-200",
  "sangat tinggi": "bg-red-50 text-red-700 border-red-200"
};

/* ── Heatmap Layer (imperative Leaflet plugin) ── */
function HeatmapLayer({ points, visible }) {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }
    if (!visible || points.length === 0) return;

    const heatData = points.map(p => {
      let statusWeight = 0.2;
      if (p.level === "waspada") statusWeight = 0.5;
      else if (p.level === "tinggi") statusWeight = 0.8;
      else if (p.level === "sangat tinggi") statusWeight = 1.0;

      return [p.lat, p.lng, statusWeight];
    });

    layerRef.current = L.heatLayer(heatData, {
      radius: 35,
      blur: 25,
      maxZoom: 12,
      max: 1.0,
      gradient: {
        0.0: "#10B981",
        0.35: "#34D399",
        0.55: "#FBBF24",
        0.75: "#F59E0B",
        0.9: "#EF4444",
        1.0: "#DC2626",
      },
    }).addTo(map);

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
      }
    };
  }, [map, points, visible]);

  return null;
}

/* ── Map Resize Trigger Component ── */
function MapResizeTrigger({ trigger }) {
  const map = useMap();
  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 150);
  }, [map, trigger]);
  return null;
}

/* ── Main Page Component ─────────────────────── */
export default function HeatmapStokPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterKota, setFilterKota] = useState("");
  const [filterLevel, setFilterLevel] = useState("");
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showMarkers, setShowMarkers] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [ews, setEws] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterKota) params.kota = filterKota;
      if (filterLevel) params.stok_level = filterLevel;
      const { data: result } = await api.get("/heatmap/stok", { params });
      setData(result);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKota, filterLevel]);

  // Status EWS dipakai bersama dengan menu Buat Pesanan; cukup diambil sekali.
  useEffect(() => {
    (async () => {
      try {
        const { data: hasil } = await api.get("/master-data/ews");
        setEws(hasil.items || []);
      } catch {
        setEws([]);
      }
    })();
  }, []);

  const suppliers = data?.suppliers || [];
  const summary = data?.summary || {};
  const kotaList = data?.summary?.kota_summary || [];

  // Heatmap data points
  const heatPoints = suppliers.map(s => ({
    lat: s.lat,
    lng: s.lng,
    stok: s.total_stok,
    level: s.stok_level
  }));

  return (
    <div className="space-y-6" data-testid="heatmap-stok-page">
      {/* ── Header ── */}
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="kedan-label text-slate-500">Monitoring Komoditas</div>
          <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">
            Monitoring Komoditas & Inflasi Harga Pangan
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Visualisasi persebaran supplier, kondisi kewajaran harga komoditas pangan dibanding acuan Bank Indonesia di wilayah Sumatera Utara.
          </p>
        </div>
        {!loading && data && (
          <div className="kedan-card p-3 flex gap-4 text-xs font-mono shrink-0 border-blue-100 bg-blue-50/20">
            <div>
              <div className="text-slate-400 uppercase tracking-wider text-[9px]">Mean Deviasi</div>
              <div className="font-semibold text-slate-800 text-sm mt-0.5">
                {data.mean_dev > 0 ? "+" : ""}{formatRupiah(data.mean_dev || 0)}
              </div>
            </div>
            <div className="w-px bg-slate-200" />
            <div>
              <div className="text-slate-400 uppercase tracking-wider text-[9px]">Standar Deviasi (SD)</div>
              <div className="font-semibold text-blue-700 text-sm mt-0.5">{formatRupiah(data.std_dev || 0)}</div>
            </div>
          </div>
        )}
      </header>

      {/* ── BI Reference Prices (komponen bersama dengan Buat Pesanan) ── */}
      {!loading && <BIPricePanel prices={data?.bi_prices} />}

      {/* ── Status EWS (komponen bersama dengan Buat Pesanan) ── */}
      <EwsTable items={ews} />

      {/* ── Stat Cards ── */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Store}
            label="Total Supplier"
            value={formatNumber(summary.total_supplier)}
            sub={`${summary.total_produk_aktif || 0} produk aktif`}
            accent="blue"
            testid="hm-stat-total"
          />
          <StatCard
            icon={CheckCircle2}
            label="Status Normal"
            value={formatNumber(summary.supplier_normal || 0)}
            sub="Harga aman & kompetitif"
            accent="emerald"
            testid="hm-stat-aman"
          />
          <StatCard
            icon={TrendingDown}
            label="Status Waspada"
            value={formatNumber(summary.supplier_waspada || 0)}
            sub="Perlu pemantauan berkala"
            accent="amber"
            testid="hm-stat-rendah"
          />
          <StatCard
            icon={AlertTriangle}
            label="Harga Tinggi"
            value={formatNumber((summary.supplier_tinggi || 0) + (summary.supplier_sangat_tinggi || 0))}
            sub={`${summary.supplier_tinggi || 0} Tinggi · ${summary.supplier_sangat_tinggi || 0} Sangat Tinggi`}
            accent="rose"
            testid="hm-stat-kritis"
          />
        </div>
      )}

      {/* ── Filters ── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-600">Filter:</span>
        </div>
        <select
          className="kedan-input !w-44"
          value={filterKota}
          onChange={(e) => setFilterKota(e.target.value)}
          data-testid="hm-filter-kota"
        >
          <option value="">Semua Kota</option>
          {["Medan", "Binjai", "Pematangsiantar", "Tebing Tinggi", "Tanjung Balai", "Sibolga", "Padang Sidempuan", "Gunungsitoli"].map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          className="kedan-input !w-44"
          value={filterLevel}
          onChange={(e) => setFilterLevel(e.target.value)}
          data-testid="hm-filter-level"
        >
          <option value="">Semua Status</option>
          <option value="normal">🟢 Normal</option>
          <option value="waspada">🟡 Waspada</option>
          <option value="tinggi">🟠 Tinggi</option>
          <option value="sangat tinggi">🔴 Sangat Tinggi</option>
        </select>

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setShowMarkers(v => !v)}
            className={`kedan-btn-secondary !py-1.5 !px-3 text-xs ${showMarkers ? "!bg-blue-50 !border-blue-200 !text-[#045498]" : ""}`}
            data-testid="hm-toggle-markers"
          >
            {showMarkers ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            Marker
          </button>
          <button
            onClick={() => setIsFullscreen(v => !v)}
            className={`kedan-btn-secondary !py-1.5 !px-3 text-xs ${isFullscreen ? "!bg-blue-50 !border-blue-200 !text-[#045498]" : ""}`}
            data-testid="hm-toggle-fullscreen"
          >
            {isFullscreen ? <Minimize className="w-3.5 h-3.5" /> : <Maximize className="w-3.5 h-3.5" />}
            Fullscreen
          </button>
        </div>
      </div>

      {/* ── Map + Sidebar ── */}
      <Section
        title="Peta Kewajaran Harga Komoditas · Sumatera Utara"
        subtitle={`${suppliers.length} supplier terpantau`}
        action={
          <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap" data-testid="hm-legend">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: LEVEL_COLOR.normal }} /> Normal
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: LEVEL_COLOR.waspada }} /> Waspada
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: LEVEL_COLOR.tinggi }} /> Tinggi
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: LEVEL_COLOR["sangat tinggi"] }} /> Sangat Tinggi
            </span>
          </div>
        }
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Map */}
          <div className={isFullscreen ? "fixed inset-0 z-[9999] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6" : "lg:col-span-2"}>
            <div
              className={`rounded-lg overflow-hidden border border-slate-200 bg-white shadow-xl flex flex-col ${isFullscreen ? "w-full h-full" : ""}`}
              style={{ height: isFullscreen ? "100%" : 520 }}
              data-testid="hm-map"
            >
              {isFullscreen && (
                <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between shrink-0">
                  <div>
                    <h2 className="text-base font-display font-semibold text-slate-800">Peta Kewajaran Harga Komoditas · Sumatera Utara</h2>
                    <p className="text-xs text-slate-500 mt-0.5">{suppliers.length} supplier terpantau</p>
                  </div>
                  <button
                    onClick={() => setIsFullscreen(false)}
                    className="kedan-btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5 hover:!bg-red-50 hover:!text-red-700 hover:!border-red-200 transition-colors"
                  >
                    <Minimize className="w-3.5 h-3.5" />
                    Keluar Fullscreen
                  </button>
                </div>
              )}
              <div className="flex-1 relative h-full w-full">
                <MapContainer
                  center={SUMUT_CENTER}
                  zoom={8}
                  minZoom={7}
                  maxZoom={14}
                  maxBounds={SUMUT_BOUNDS}
                  maxBoundsViscosity={1.0}
                  style={{ height: "100%", width: "100%" }}
                  scrollWheelZoom
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  {/* Heatmap overlay */}
                  <HeatmapLayer points={heatPoints} visible={showHeatmap} />

                  {/* Trigger resize helper when fullscreen changes */}
                  <MapResizeTrigger trigger={isFullscreen} />

                  {/* Circle markers */}
                  {showMarkers && suppliers.map((s) => {
                    const color = LEVEL_COLOR[s.stok_level] || LEVEL_COLOR.normal;
                    const radius = s.stok_level === "sangat tinggi" ? 12 : s.stok_level === "tinggi" ? 11 : s.stok_level === "waspada" ? 10 : 9;
                    return (
                      <CircleMarker
                        key={s.id}
                        center={[s.lat, s.lng]}
                        radius={radius}
                        pathOptions={{
                          color,
                          fillColor: color,
                          fillOpacity: s.verified ? 0.85 : 0.5,
                          weight: (s.stok_level === "tinggi" || s.stok_level === "sangat tinggi") ? 3 : 2,
                        }}
                      >
                        {/* Tooltip */}
                        <Tooltip
                          direction="top"
                          permanent
                          offset={[0, -radius - 2]}
                          className="map-ticket-badge"
                        >
                          <span style={{
                            backgroundColor: color,
                            color: "white",
                            padding: "1px 6px",
                            borderRadius: "9999px",
                            fontSize: "10px",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                          }}>
                            {LEVEL_LABEL[s.stok_level]}
                          </span>
                        </Tooltip>
                        <Popup>
                          <div className="text-sm min-w-[300px] max-w-[350px]">
                            <div className="font-semibold text-slate-900">{s.nama_usaha}</div>
                            <div className="text-slate-500 text-xs mt-0.5">
                              {s.kota}{s.kecamatan ? ` · ${s.kecamatan}` : ""}
                            </div>
                            <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                              <span style={{
                                display: "inline-flex", alignItems: "center",
                                padding: "1px 6px", borderRadius: "9999px",
                                fontSize: "10px", fontWeight: 600,
                                backgroundColor: s.verified ? "#D1FAE5" : "#F1F5F9",
                                color: s.verified ? "#065F46" : "#475569",
                                border: `1px solid ${s.verified ? "#A7F3D0" : "#CBD5E1"}`,
                              }}>
                                {s.verified ? "Terverifikasi" : "Belum Verifikasi"}
                              </span>
                              <span style={{
                                display: "inline-flex", alignItems: "center",
                                padding: "1px 6px", borderRadius: "9999px",
                                fontSize: "10px", fontWeight: 600,
                                backgroundColor: s.stok_level === "normal" ? "#D1FAE5" : s.stok_level === "waspada" ? "#FEF3C7" : s.stok_level === "tinggi" ? "#FFEDD5" : "#FEE2E2",
                                color: s.stok_level === "normal" ? "#065F46" : s.stok_level === "waspada" ? "#92400E" : s.stok_level === "tinggi" ? "#C2410C" : "#991B1B",
                                border: `1px solid ${s.stok_level === "normal" ? "#A7F3D0" : s.stok_level === "waspada" ? "#FDE68A" : s.stok_level === "tinggi" ? "#FED7AA" : "#FECACA"}`,
                              }}>
                                {LEVEL_LABEL[s.stok_level]}
                              </span>
                            </div>

                            {/* Stats */}
                            <div className="mt-3 pt-2.5 border-t border-slate-200">
                              <div className="grid grid-cols-3 gap-1">
                                <MiniStat label="Aktif" value={s.produk_aktif} color="#065F46" bg="#D1FAE5" border="#A7F3D0" />
                                <MiniStat label="Stok" value={formatNumber(s.total_stok)} color="#1E40AF" bg="#DBEAFE" border="#BFDBFE" />
                                <MiniStat label="Nilai" value={formatNumber(s.total_produk)} color="#475569" bg="#F1F5F9" border="#CBD5E1" />
                              </div>
                            </div>

                            {/* Product breakdown with BI comparison */}
                            {s.products_detail && s.products_detail.length > 0 && (
                              <div className="mt-3 pt-2 border-t border-slate-200">
                                <div style={{
                                  fontSize: "10px", fontWeight: 600, color: "#64748B",
                                  textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6,
                                }}>
                                  Detail Harga & Status (STATA SD)
                                </div>
                                <div className="max-h-[160px] overflow-y-auto space-y-1.5 pr-1">
                                  {s.products_detail.map(p => (
                                    <div key={p.id} className="flex flex-col text-xs p-1.5 rounded border border-slate-100 bg-slate-50/50">
                                      <div className="flex items-center justify-between font-medium">
                                        <span className="text-slate-800 truncate max-w-[150px]">{p.nama}</span>
                                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold border ${LEVEL_BG[p.status]}`}>
                                          {LEVEL_LABEL[p.status]}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1">
                                        <span>Stok: {p.stok}</span>
                                        <span>
                                          {formatRupiah(p.harga)} <span className="text-slate-400">/ BI: {p.ref_harga > 0 ? formatRupiah(p.ref_harga) : "-"}</span>
                                        </span>
                                      </div>
                                      {p.ref_harga > 0 && (
                                        <div className="flex items-center justify-between text-[9px] mt-1.5 border-t border-slate-200/50 pt-1 text-slate-400 font-mono">
                                          <span>Deviasi: <span className={p.deviation > 0 ? "text-red-500 font-semibold" : "text-emerald-600 font-semibold"}>{p.deviation > 0 ? "+" : ""}{formatRupiah(p.deviation)}</span></span>
                                          <span>Indikator: <span className="font-semibold text-slate-600">{p.indicator} SD</span></span>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Total value */}
                            <div className="mt-2.5 pt-2 border-t border-slate-200 flex items-center justify-between">
                              <span style={{ fontSize: "10px", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                Nilai Stok
                              </span>
                              <span className="font-mono text-xs font-semibold text-slate-800">
                                {formatRupiah(s.nilai_stok)}
                              </span>
                            </div>

                            <Link
                              to={`/suppliers/${s.id}`}
                              style={{ color: "#045498", fontSize: 11, fontWeight: 600, textDecoration: "underline", marginTop: 6, display: "inline-block" }}
                            >
                              Lihat detail supplier →
                            </Link>
                          </div>
                        </Popup>
                      </CircleMarker>
                    );
                  })}
                </MapContainer>
              </div>
            </div>
          </div>

          {/* ── Sidebar: City Summary + Kritis List ── */}
          <div className="space-y-4">
            {/* City Summary */}
            <div className="kedan-card overflow-hidden" data-testid="hm-city-summary">
              <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between">
                <div>
                  <div className="kedan-label text-slate-500">Ringkasan Per Kota</div>
                  <div className="font-display font-semibold text-slate-900 text-sm mt-0.5">{kotaList.length} Kota</div>
                </div>
                <Layers className="w-4 h-4 text-slate-400" />
              </div>
              <div className="divide-y divide-slate-100 max-h-[240px] overflow-y-auto">
                {kotaList.length === 0 && (
                  <div className="px-5 py-6 text-sm text-slate-400 text-center">Tidak ada data.</div>
                )}
                {kotaList.map(k => (
                  <div key={k.kota} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                    <div>
                      <div className="text-sm font-medium text-slate-800">{k.kota}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{k.supplier_count} supplier</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Mini level breakdown */}
                      {k.detail && (
                        <div className="flex items-center gap-1">
                          {k.detail.normal > 0 && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100" title="Normal">
                              {k.detail.normal}
                            </span>
                          )}
                          {k.detail.waspada > 0 && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100" title="Waspada">
                              {k.detail.waspada}
                            </span>
                          )}
                          {k.detail.tinggi > 0 && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-100" title="Tinggi">
                              {k.detail.tinggi}
                            </span>
                          )}
                          {k.detail["sangat tinggi"] > 0 && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-100" title="Sangat Tinggi">
                              {k.detail["sangat tinggi"]}
                            </span>
                          )}
                        </div>
                      )}
                      <span className={`kedan-badge text-[10px] ${LEVEL_BG[k.stok_level]}`}>
                        {LEVEL_LABEL[k.stok_level]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Kritis Suppliers */}
            <div className="kedan-card overflow-hidden" data-testid="hm-kritis-list">
              <div className="px-5 py-3 border-b border-slate-200 bg-red-50/30 flex items-center justify-between">
                <div>
                  <div className="kedan-label text-red-700">Perlu Tindakan</div>
                  <div className="font-display font-semibold text-slate-900 text-sm mt-0.5">Supplier Harga Tinggi</div>
                </div>
                <AlertTriangle className="w-4 h-4 text-red-500" />
              </div>
              <div className="divide-y divide-slate-100 max-h-[240px] overflow-y-auto">
                {suppliers.filter(s => s.stok_level === "tinggi" || s.stok_level === "sangat tinggi").length === 0 && (
                  <div className="px-5 py-6 text-sm text-slate-400 text-center flex flex-col items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    Tidak ada supplier berstatus tinggi / sangat tinggi.
                  </div>
                )}
                {suppliers.filter(s => s.stok_level === "tinggi" || s.stok_level === "sangat tinggi").map(s => (
                  <Link
                    key={s.id}
                    to={`/suppliers/${s.id}`}
                    className="px-5 py-3 hover:bg-red-50/40 flex items-center gap-3 transition-colors"
                    data-testid={`hm-kritis-${s.id}`}
                  >
                    <div className="w-8 h-8 rounded-md bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                      <AlertTriangle className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{s.nama_usaha}</div>
                      <div className="text-xs text-slate-500">{s.kota} · {s.produk_aktif} produk aktif</div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-semibold border ${LEVEL_BG[s.stok_level]}`}>
                        {LEVEL_LABEL[s.stok_level]}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Quick actions */}
            <div className="kedan-card p-4 space-y-2" data-testid="hm-quick-actions">
              <Link
                to="/suppliers"
                className="flex items-center justify-between text-sm text-[#045498] hover:underline font-medium"
              >
                Lihat semua supplier <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link
                to="/peta"
                className="flex items-center justify-between text-sm text-[#045498] hover:underline font-medium"
              >
                Peta distribusi lengkap <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* ── Stok Level Distribution Bar ── */}
      {!loading && summary.total_supplier > 0 && (
        <Section
          title="Distribusi Kondisi Komoditas"
          subtitle="Proporsi supplier berdasarkan tingkat kewajaran harga komoditas (STATA SD)"
        >
          <div className="kedan-card p-6">
            {/* Stacked bar */}
            <div className="flex rounded-full overflow-hidden h-4 mb-4" data-testid="hm-bar">
              {summary.supplier_normal > 0 && (
                <div
                  className="transition-all duration-500"
                  style={{
                    width: `${(summary.supplier_normal / summary.total_supplier) * 100}%`,
                    backgroundColor: LEVEL_COLOR.normal,
                  }}
                  title={`Normal: ${summary.supplier_normal}`}
                />
              )}
              {summary.supplier_waspada > 0 && (
                <div
                  className="transition-all duration-500"
                  style={{
                    width: `${(summary.supplier_waspada / summary.total_supplier) * 100}%`,
                    backgroundColor: LEVEL_COLOR.waspada,
                  }}
                  title={`Waspada: ${summary.supplier_waspada}`}
                />
              )}
              {summary.supplier_tinggi > 0 && (
                <div
                  className="transition-all duration-500"
                  style={{
                    width: `${(summary.supplier_tinggi / summary.total_supplier) * 100}%`,
                    backgroundColor: LEVEL_COLOR.tinggi,
                  }}
                  title={`Tinggi: ${summary.supplier_tinggi}`}
                />
              )}
              {summary.supplier_sangat_tinggi > 0 && (
                <div
                  className="transition-all duration-500"
                  style={{
                    width: `${(summary.supplier_sangat_tinggi / summary.total_supplier) * 100}%`,
                    backgroundColor: LEVEL_COLOR["sangat tinggi"],
                  }}
                  title={`Sangat Tinggi: ${summary.supplier_sangat_tinggi}`}
                />
              )}
            </div>

            {/* Legend labels */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { key: "normal", label: "Normal", icon: CheckCircle2, count: summary.supplier_normal || 0, desc: "Indikator deviasi di bawah 0.25 SD (<0.25 SD)" },
                { key: "waspada", label: "Waspada", icon: TrendingDown, count: summary.supplier_waspada || 0, desc: "Indikator deviasi di level 0.25 - 1.0 SD (0.25 <= Waspada < 1.0 SD)" },
                { key: "tinggi", label: "Tinggi", icon: AlertTriangle, count: summary.supplier_tinggi || 0, desc: "Indikator deviasi di level 1.0 - 2.0 SD (1.0 <= Tinggi < 2.0 SD)" },
                { key: "sangat tinggi", label: "Sangat Tinggi", icon: AlertTriangle, count: summary.supplier_sangat_tinggi || 0, desc: "Indikator deviasi di atas 2.0 SD (Sangat Tinggi >= 2.0 SD)" },
              ].map(item => (
                <div key={item.key} className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                    style={{
                      backgroundColor: item.key === "normal" ? "#D1FAE5" : item.key === "waspada" ? "#FEF3C7" : item.key === "tinggi" ? "#FFEDD5" : "#FEE2E2",
                      color: LEVEL_COLOR[item.key],
                    }}
                  >
                    <item.icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      {item.count} Supplier
                      <span className="text-xs font-normal text-slate-500 ml-1">
                        ({summary.total_supplier > 0 ? Math.round((item.count / summary.total_supplier) * 100) : 0}%)
                      </span>
                    </div>
                    <div className="text-xs font-medium text-slate-700">{item.label}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}

/* ── Mini Stat Badge (popup) ── */
function MiniStat({ label, value, color, bg, border }) {
  return (
    <div style={{
      backgroundColor: bg, border: `1px solid ${border}`,
      borderRadius: 4, padding: "3px 4px", textAlign: "center",
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 9, color, opacity: 0.8, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
    </div>
  );
}
